import debounce from 'lodash/debounce';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Input } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ICompareCondition, IDateCondition } from '@tymely/atoms';
import { CONDITION_EDITOR_DEBOUNCE_INTERVAL_MS } from '@tymely/config';
import { Nullable } from '@global/types';
import { offsetTimeZone } from '@tymely/utils';

import { BaseConditionEditor, ConditionEditorProps } from './BaseConditionEditor';

export const SingleValueConditionEditor = memo((props: ConditionEditorProps<ICompareCondition>) => {
    const [argValue, setArgValue] = useState(props.condition.value?.value);

    useEffect(() => {
        setArgValue(props.condition.value?.value);
    }, [props.condition.value?.value]);

    const setCondition = useMemo(
        () =>
            debounce((value: typeof props.condition.value.value) => {
                props.setCondition({
                    argument_metadata: props.argumentMetadata,
                    argument_metadata_id: props.argumentMetadata.id,
                    ...props.condition,
                    value: { ...props.condition.value, value },
                });
            }, CONDITION_EDITOR_DEBOUNCE_INTERVAL_MS),
        [props.condition, props.argumentMetadata, props.setCondition],
    );

    const handleValueChange = useCallback(
        (value: typeof props.condition.value.value) => {
            setArgValue(value);
            setCondition(value);
        },
        [setCondition],
    );

    return (
        <BaseConditionEditor {...props}>
            <Input
                value={argValue ?? ''}
                sx={{
                    pr: 2,
                    borderRadius: '4px',
                    px: 1,
                    py: 1,
                    width: `clamp(4ch, ${Math.max(4, argValue?.toString().length ?? 0)}ch, 16ch)`,
                    '&:hover': {
                        backgroundColor: props.disabled ? 'transparent' : (theme) => theme.palette.grey[200],
                    },
                    '&:focus-within': {
                        backgroundColor: props.disabled ? 'transparent' : (theme) => theme.palette.grey[200],
                    },
                }}
                onChange={(e) => handleValueChange(e.target.value)}
                inputProps={{ sx: { p: 0 } }}
                disabled={props.disabled}
                disableUnderline={props.disableUnderline ?? true}
                onFocus={() => props.onInputFocusChange?.(true)}
                onBlur={() => props.onInputFocusChange?.(false)}
            />
        </BaseConditionEditor>
    );
});

SingleValueConditionEditor.displayName = 'SingleValueConditionEditor';

export const SingleDateValueConditionEditor = memo((props: ConditionEditorProps<IDateCondition>) => {
    const [date, setDate] = useState(
        props.condition.value.value ? offsetTimeZone(new Date(props.condition.value.value)) : null,
    );

    useEffect(() => {
        setDate(props.condition.value.value ? offsetTimeZone(new Date(props.condition.value.value as string)) : null);
    }, [props.condition.value?.value]);

    const setCondition = useMemo(
        () =>
            debounce((value: typeof props.condition.value.value) => {
                props.setCondition({
                    argument_metadata: props.argumentMetadata,
                    argument_metadata_id: props.argumentMetadata.id,
                    ...props.condition,
                    value: { ...props.condition.value, value },
                });
            }, CONDITION_EDITOR_DEBOUNCE_INTERVAL_MS),
        [props.condition, props.argumentMetadata, props.setCondition],
    );

    const handleValueChange = useCallback(
        (newDate: Nullable<Date>) => {
            if (!newDate) return;

            newDate = offsetTimeZone(newDate, false);

            setDate(newDate);
            setCondition(newDate.toISOString());
        },
        [setCondition],
    );

    return (
        <BaseConditionEditor {...props}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                    label="Choose date"
                    format="dd-MM-yyyy"
                    value={date}
                    onChange={handleValueChange}
                    closeOnSelect
                    disabled={props.disabled}
                    enableAccessibleFieldDOMStructure={false}
                    slotProps={{
                        textField: {
                            inputProps: {
                                readOnly: true,
                            },
                            label: null,
                            sx: {
                                pr: 2,
                                '& .MuiOutlinedInput-notchedOutline': { borderStyle: 'none' },
                                backgroundColor: props.disabled ? 'transparent' : (theme) => theme.palette.grey[200],
                                borderRadius: '4px',
                                '&:hover': {
                                    backgroundColor: props.disabled
                                        ? 'transparent'
                                        : (theme) => theme.palette.grey[300],
                                },
                                '&:focus-within': {
                                    backgroundColor: props.disabled
                                        ? 'transparent'
                                        : (theme) => theme.palette.grey[300],
                                },
                            },
                            disabled: props.disabled,
                            onFocus: () => props.onInputFocusChange?.(true),
                            onBlur: () => props.onInputFocusChange?.(false),
                        },
                    }}
                />
            </LocalizationProvider>
        </BaseConditionEditor>
    );
});

SingleDateValueConditionEditor.displayName = 'SingleDateValueConditionEditor';
